<template>
    <div v-if="hasData" class="credit-status" :class="'credit-status--' + level">
        <div class="credit-status-head">
            <i class="material-icons md-18 credit-status-icon">{{ level === 'ok' ? 'verified_user' : 'report_problem' }}</i>
            <span class="credit-status-title">{{ headline }}</span>
            <router-link v-if="customerId" :to="'/customers/' + customerId" class="credit-status-link"
                title="Otwórz kartotekę klienta (ustawienia limitów w ERP)">
                <i class="material-icons md-14">open_in_new</i>
            </router-link>
        </div>

        <div v-if="level !== 'ok'" class="credit-status-metrics">
            <div class="credit-metric">
                <div class="credit-metric-label">Nierozliczone (kredytowy)</div>
                <div class="credit-metric-value">
                    {{ money(unsettled) }} zł <span class="credit-metric-limit">/ {{ money(limitUnsettled) }} zł</span>
                </div>
                <div class="credit-bar">
                    <div class="credit-bar-fill" :class="barClass(creditUsagePct)" :style="{ width: barWidth(creditUsagePct) }"></div>
                </div>
                <div class="credit-metric-pct">
                    Wykorzystanie: <strong>{{ creditUsagePct != null ? creditUsagePct + '%' : 'brak limitu' }}</strong>
                </div>
            </div>

            <div class="credit-metric" v-if="late > 0">
                <div class="credit-metric-label">Po terminie (przeterminowane)</div>
                <div class="credit-metric-value">
                    {{ money(late) }} zł <span class="credit-metric-limit">/ {{ money(limitLate) }} zł</span>
                </div>
                <div class="credit-bar">
                    <div class="credit-bar-fill" :class="barClass(overdueUsagePct)" :style="{ width: barWidth(overdueUsagePct) }"></div>
                </div>
                <div class="credit-metric-pct">
                    Wykorzystanie: <strong>{{ overdueUsagePct != null ? overdueUsagePct + '%' : 'brak limitu' }}</strong>
                </div>
            </div>

            <div class="credit-chips" v-if="late > 0">
                <span class="credit-chip" title="Maksymalna liczba dni przeterminowania (najstarsza faktura)">
                    <i class="material-icons md-14">schedule</i> {{ latePaymentsDays }} dni po terminie
                </span>
                <span class="credit-chip" title="Liczba przeterminowanych faktur">
                    <i class="material-icons md-14">description</i> {{ latePaymentsCount }} FV przeterminowanych
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CustomerCreditStatus',
    props: {
        // Obiekt customer.payments (unsettledPayments, latePayments, limity, liczniki, dni)
        payments: { type: Object, default: null },
        // Gdy podany – wyświetla link do kartoteki klienta w ERP
        customerId: { type: String, default: '' },
        // Ukryj stan "brak zaległości" (np. w kompaktowym widoku ticketu)
        hideOk: { type: Boolean, default: false }
    },
    computed: {
        p() { return this.payments || {}; },
        unsettled() { return Number(this.p.unsettledPayments) || 0; },
        late() { return Number(this.p.latePayments) || 0; },
        limitUnsettled() { return Number(this.p.limitUnsettledPayments) || 0; },
        limitLate() { return Number(this.p.limitLatePayments) || 0; },
        latePaymentsDays() { return Number(this.p.latePaymentsDays) || 0; },
        latePaymentsCount() { return Number(this.p.latePaymentsCount) || 0; },
        // Poziom zgodny z legendą w kartotece (WfirmaUnsettledPayments.vue)
        level() {
            if (this.unsettled <= 0) return 'ok';
            if (this.unsettled > this.limitUnsettled || this.late > this.limitLate) return 'red';
            if (this.late > 0 && this.late <= this.limitLate && this.unsettled <= this.limitUnsettled) return 'yellow';
            return 'blue';
        },
        hasData() {
            if (!this.payments) return false;
            if (this.level === 'ok') return !this.hideOk;
            return true;
        },
        headline() {
            switch (this.level) {
                case 'red': return 'Przekroczony limit płatności!';
                case 'yellow': return 'Klient posiada płatności po terminie';
                case 'blue': return 'Klient posiada nierozliczone płatności';
                default: return 'Brak zaległości płatniczych';
            }
        },
        creditUsagePct() {
            if (this.limitUnsettled > 0) return Math.round((this.unsettled / this.limitUnsettled) * 100);
            return this.unsettled > 0 ? null : 0;
        },
        overdueUsagePct() {
            if (this.limitLate > 0) return Math.round((this.late / this.limitLate) * 100);
            return this.late > 0 ? null : 0;
        }
    },
    methods: {
        money(val) {
            const n = Number(val) || 0;
            return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        barWidth(pct) {
            if (pct == null) return '100%';
            return Math.min(100, Math.max(0, pct)) + '%';
        },
        barClass(pct) {
            if (pct == null || pct >= 100) return 'credit-bar-fill--danger';
            if (pct >= 80) return 'credit-bar-fill--warn';
            return 'credit-bar-fill--ok';
        }
    }
};
</script>

<style scoped>
.credit-status {
    border: 1px solid #e5e7eb;
    border-left-width: 4px;
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 8px;
    background: var(--dm-surface, #fff);
    font-size: 12px;
}
.credit-status--ok { border-left-color: #22c55e; background: var(--dm-tint-green, #f0fdf4); }
.credit-status--blue { border-left-color: #3b82f6; background: var(--dm-tint-blue, #eff6ff); }
.credit-status--yellow { border-left-color: #f59e0b; background: var(--dm-tint-amber, #fffbeb); }
.credit-status--red { border-left-color: #ef4444; background: var(--dm-tint-red, #fef2f2); }

.credit-status-head { display: flex; align-items: center; gap: 6px; }
.credit-status-icon { flex-shrink: 0; }
.credit-status--ok .credit-status-icon { color: #16a34a; }
.credit-status--blue .credit-status-icon { color: #2563eb; }
.credit-status--yellow .credit-status-icon { color: #d97706; }
.credit-status--red .credit-status-icon { color: #dc2626; }
.credit-status-title { font-weight: 600; color: var(--dm-text, #374151); }
.credit-status-link { margin-left: auto; color: var(--dm-text-muted, #9ca3af); display: inline-flex; align-items: center; }
.credit-status-link:hover { color: var(--dm-on-blue, #2563eb); }

.credit-status-metrics { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.credit-metric-label { color: var(--dm-text-muted, #6b7280); font-size: 11px; }
.credit-metric-value { font-weight: 600; color: var(--dm-text, #374151); }
.credit-metric-limit { font-weight: 400; color: var(--dm-text-muted, #9ca3af); }
.credit-metric-pct { font-size: 11px; color: var(--dm-text-muted, #6b7280); margin-top: 2px; }

.credit-bar { height: 6px; border-radius: 3px; background: var(--dm-surface-3, #e5e7eb); overflow: hidden; margin-top: 3px; }
.credit-bar-fill { height: 100%; border-radius: 3px; transition: width 0.3s; }
.credit-bar-fill--ok { background: #22c55e; }
.credit-bar-fill--warn { background: #f59e0b; }
.credit-bar-fill--danger { background: #ef4444; }

.credit-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.credit-chip {
    display: inline-flex; align-items: center; gap: 3px;
    background: var(--dm-surface-2, #f3f4f6); color: var(--dm-text, #374151);
    border-radius: 10px; padding: 2px 8px; font-size: 11px;
}
.credit-status--red .credit-chip { background: rgba(239,68,68,0.12); color: #b91c1c; }
.credit-status--yellow .credit-chip { background: rgba(245,158,11,0.14); color: #b45309; }
</style>
